/**
 * The scroll vocabulary of the kit — one class string per kind of scroller.
 *
 * Every scroller in the portal composes one of these instead of spelling
 * `overflow-y-auto` itself, for the reason P10.1 exists: the classes that make a
 * scroller behave are not obvious, they are easy to half-apply, and half-applied
 * is exactly how a dialog clips its own content or drags the page behind it.
 *
 * What each part is doing:
 *
 *   overflow-y-auto      a bar appears only when there is something to scroll.
 *   overscroll-contain   the scroll chain STOPS here. Without it, reaching the
 *                        bottom of a dialog's body hands the next wheel tick to
 *                        the page underneath, which then scrolls behind the
 *                        overlay — the classic "the modal is open and the page
 *                        moved" bug (U0: an overlay contains its own content).
 *   min-h-0              a flex child refuses to shrink below its content unless
 *                        told it may. Without it a body grows to its natural
 *                        height, pushes past the clamp above it, and the clamp
 *                        silently stops meaning anything. Load-bearing, and the
 *                        single most common omission.
 *   flex-1               the body takes the space the header and footer leave.
 *
 * The bar's own SKIN is not here: `globals.css` styles every scroller in the
 * document (both engines, both themes), because an `overflow-*` utility can
 * create a scroller anywhere and a component-level skin would only cover the
 * ones we remembered. These constants govern behaviour; the stylesheet governs
 * appearance.
 */

/**
 * The body of an overlay — `DialogBody`, `SheetBody`, any pane that fills the
 * space between a pinned header and a pinned footer.
 */
export const SCROLLER_CLASSES = 'min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-thin';

/**
 * The list inside a popper (dropdown, select, command palette, picker panel).
 *
 * No `flex-1`/`min-h-0`: a popper list is bounded by its own `max-h-*-available`
 * clamp rather than by a flex parent, and `max-h` on a `flex-1` child is one more
 * way for the two to disagree.
 */
export const POPPER_SCROLLER_CLASSES = 'overflow-y-auto overscroll-contain scrollbar-thin';

/**
 * A rail that scrolls sideways — a wide table, a strip of chips.
 *
 * `overscroll-x-contain` matters more here than on the block axis: a horizontal
 * trackpad fling that reaches the last column and keeps going is a browser BACK
 * navigation out of the page the operator was reading.
 *
 * **`contain: paint` is load-bearing, not an optimization** (P10.5c2). Without
 * it, a `table-layout: auto` table inside this scroller propagates its
 * *min-content* width to the DOCUMENT's scrollable overflow in LTR — so the page
 * itself scrolled sideways behind a scroller that was visually containing the
 * table perfectly. Measured on a 320px viewport: `/integrations` 536 vs a 309
 * page, `/admins/staff` (shipped in P9, untouched) 623 — and `/settings`, which
 * holds no table, clean. Of `max-width:100%`, `contain:inline-size`, `width:100%`
 * and `min-width:0`, **only `contain: paint` closed it**, and it is exactly the
 * promise this class already makes: nothing inside escapes the box. It creates a
 * stacking context, which costs nothing here because every overlay a row can open
 * (dropdown, tooltip, dialog) is portalled out of the table.
 */
export const HORIZONTAL_SCROLLER_CLASSES =
  'overflow-x-auto overscroll-x-contain scrollbar-thin [contain:paint]';

/**
 * The page pane — the app shell's main region, and the only scroller in the
 * portal that is NOT inside something.
 *
 * Deliberately without `overscroll-contain`: there is nothing behind it to
 * protect, and containing the chain here would cancel the browser's own
 * pull-to-refresh on a phone. `scrollbar-gutter-stable` reserves the bar's width
 * whether or not it is showing, so navigating from a short page to a long one
 * does not shift the whole layout sideways.
 */
export const PAGE_SCROLLER_CLASSES = 'min-h-0 flex-1 overflow-y-auto scrollbar-gutter-stable';

/**
 * The separators an overlay body shows at its edges.
 *
 * They appear ONLY while there is content past that edge, driven by the
 * `data-scroll-start` / `data-scroll-end` attributes `useScrollEdges` writes: a
 * line under the header means "there is more above", a line over the footer means
 * "there is more below". A permanently drawn separator says nothing — and its
 * absence is what made a scrolled body indistinguishable from a clipped one.
 */
export const SCROLL_EDGE_CLASSES =
  'data-[scroll-start=false]:border-t data-[scroll-end=false]:border-b border-border';
